// client/src/features/salesCoach/components/RepLearningInterface.jsx
import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { createPortal } from "react-dom";
import { salesCoachApi } from "../api/salesCoachApi";
import {
  ArrowLeft,
  BookOpen,
  UserCheck,
  Sparkles,
  CheckCircle,
  ShieldAlert,
  BarChart2,
  LogOut,
  ArrowRight,
} from "lucide-react";
import Loader from "@/shared/components/ui/Loaders/Loader";
import CommonSelect from "@/shared/components/ui/Select/CommonSelect";

// New Coach Redesign Components
import useAvatarStore from "../stores/avatarStore";
import SessionHeader from "./SessionHeader";
// import AvatarSelector from "./AvatarSelector";
import AvatarScene from "./AvatarScene";
import FloatingChatButton from "./FloatingChatButton";
import ChatPanel from "./ChatPanel";
import { AnimatePresence, motion } from "framer-motion";

const RepLearningInterface = ({ id }) => {
  const navigate = useNavigate();
  const [training, setTraining] = useState(null);
  const [personas, setPersonas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeMode, setActiveMode] = useState("learn");
  const [selectedPersonaId, setSelectedPersonaId] = useState("");

  const [session, setSession] = useState(null);
  const [trainingSessionId, setTrainingSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);

  const [showRoleplaySetup, setShowRoleplaySetup] = useState(false);
  const [switchingToRoleplay, setSwitchingToRoleplay] = useState(false);

  const [assessment, setAssessment] = useState(null);
  const [assessing, setAssessing] = useState(false);
  const [finishing, setFinishing] = useState(false);

  const messageEndRef = useRef(null);
  const speakingTimeoutRef = useRef(null);

  // Redesign Store bindings
  const selectedAvatar = useAvatarStore((state) => state.selectedAvatar);
  const setSelectedAvatar = useAvatarStore((state) => state.setSelectedAvatar);
  const sessionActive = useAvatarStore((state) => state.sessionActive);
  const setSessionActive = useAvatarStore((state) => state.setSessionActive);
  const chatOpen = useAvatarStore((state) => state.chatOpen);
  const setChatOpen = useAvatarStore((state) => state.setChatOpen);
  const setAvatarEmotion = useAvatarStore((state) => state.setAvatarEmotion);
  const setCoachState = useAvatarStore((state) => state.setCoachState);
  const resetStore = useAvatarStore((state) => state.resetStore);

  const isRoleplay = session?.mode === "roleplay";
  const hasUserTextMessages = messages.some(
    (m) => m.role === "user" && !m.content?.startsWith("__conv_id:"),
  );
  const canEndTraining = hasUserTextMessages;
  const canEndRoleplay = hasUserTextMessages;

  // Clean up store state when unmounting
  useEffect(() => {
    return () => {
      resetStore();
      if (speakingTimeoutRef.current) {
        clearTimeout(speakingTimeoutRef.current);
      }
    };
  }, [resetStore]);

  useEffect(() => {
    loadLearningData();
  }, [id]);

  useEffect(() => {
    messageEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const loadLearningData = async () => {
    setLoading(true);
    try {
      const [t, p] = await Promise.all([
        salesCoachApi.getTraining(id),
        salesCoachApi.listPersonas(),
      ]);
      setTraining(t);
      setPersonas(p);
      if (p.length > 0) setSelectedPersonaId(p[0].id);
    } catch (err) {
      console.error("Failed to load details:", err);
    } finally {
      setLoading(false);
    }
  };

  const startSession = async () => {
    if (activeMode === "roleplay" && !selectedPersonaId) return;
    setLoading(true);
    try {
      const data = await salesCoachApi.createUserSession({
        trainingId: id,
        mode: activeMode,
        roleplayPersonaId: activeMode === "roleplay" ? selectedPersonaId : null,
      });
      setSession(data.data);
      if (activeMode === "learn") setTrainingSessionId(data.data.id);
      setMessages(data.messages || []);
      setSessionActive(true);
      setChatOpen(true);
      setAvatarEmotion("greeting");
    } catch (err) {
      console.error("Failed to create session:", err);
    } finally {
      setLoading(false);
    }
  };

  const startRoleplaySession = async () => {
    if (!trainingSessionId || !selectedPersonaId) return;
    setSwitchingToRoleplay(true);
    try {
      await salesCoachApi.finishUserSession(trainingSessionId);
      const data = await salesCoachApi.createUserSession({
        trainingId: id,
        mode: "roleplay",
        roleplayPersonaId: selectedPersonaId,
        parentSessionId: trainingSessionId,
      });
      setSession(data.data);
      setMessages(data.messages || []);
      setShowRoleplaySetup(false);
      setSessionActive(true);
      setChatOpen(true);
      setAvatarEmotion("greeting");
    } catch (err) {
      console.error("Failed to start roleplay:", err);
      alert("Could not start roleplay. Please try again.");
    } finally {
      setSwitchingToRoleplay(false);
    }
  };

  const handleSend = async (textToSend = input) => {
    const resolvedText = typeof textToSend === "string" ? textToSend : input;
    const text = resolvedText.trim();
    if (!text || sending || text.startsWith("__conv_id:")) return;
    setInput("");
    setSending(true);
    setCoachState("thinking");
    setAvatarEmotion("objection");

    const tempMsg = { id: Date.now().toString(), role: "user", content: text };
    setMessages((prev) => [...prev, tempMsg]);

    try {
      const res = await salesCoachApi.sendUserMessage(session.id, text);
      setMessages((prev) => [
        ...prev.filter((m) => m.id !== tempMsg.id),
        res.userMessage,
        res.modelMessage,
      ]);

      // Procedural speaking animation for text responses
      setCoachState("speaking");
      setAvatarEmotion("explaining");

      const textLength = res.modelMessage?.content?.length || 50;
      const delay = Math.max(2500, Math.min(8000, textLength * 45));

      if (speakingTimeoutRef.current) {
        clearTimeout(speakingTimeoutRef.current);
      }
      speakingTimeoutRef.current = setTimeout(() => {
        setCoachState("idle");
        setAvatarEmotion("waiting");
      }, delay);
    } catch (err) {
      console.error("Failed to get response:", err);
      setCoachState("idle");
      setAvatarEmotion("confusion");
    } finally {
      setSending(false);
    }
  };

  const handleFinishTraining = async () => {
    setFinishing(true);
    try {
      await salesCoachApi.finishUserSession(session.id);
      navigate("/ai-workspace?tab=sales-coach");
    } catch (err) {
      console.error("Failed to finish training:", err);
      alert("Could not finish the session. Please try again.");
      setFinishing(false);
    }
  };

  const handleEndRoleplay = async () => {
    setAssessing(true);
    try {
      const data = await salesCoachApi.endUserSession(session.id);
      setAssessment(data);
    } catch {
      alert(
        "Please complete more of the roleplay before requesting your assessment.",
      );
      setAssessing(false);
    }
  };

  const handleLobbyLaunch = () => {
    setSessionActive(true);
    setChatOpen(true);
    setAvatarEmotion("greeting");
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-[calc(100vh-60px)] bg-gray-50">
        <Loader text="Setting up your session..." />
      </div>
    );
  }

  // ── 1. Session setup screen ──────────────────────────────────────────────────
  if (!session) {
    return (
      <div className="p-6 max-w-3xl mx-auto min-h-[calc(100vh-40px)] flex flex-col justify-center">
        <div className="flex items-center gap-3 pb-6 border-b border-gray-200 mb-8">
          <Link
            to="/ai-workspace?tab=sales-coach"
            className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <h1 className="text-xl font-bold text-gray-800">
            Configure Session: {training?.product_name}
          </h1>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm space-y-6">
          <div>
            <label className="block text-sm font-semibold text-gray-700">
              Choose how to start
            </label>
            <div className="grid gap-4 md:grid-cols-2 mt-3">
              {[
                {
                  value: "learn",
                  icon: BookOpen,
                  title: "Q&A Product Learning",
                  desc: "Learn with the AI coach, then optionally switch to roleplay for your assessment.",
                },
                {
                  value: "roleplay",
                  icon: UserCheck,
                  title: "Customer Roleplay",
                  desc: "Jump straight into a buyer simulation. Assessment runs when you finish the roleplay.",
                },
              ].map(({ value, icon: Icon, title, desc }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setActiveMode(value)}
                  className={`p-5 rounded-2xl border text-left flex flex-col justify-between h-40 transition-all ${
                    activeMode === value
                      ? "border-secondary bg-secondary-ultra-light/15 shadow-sm shadow-[#207C9720]"
                      : "border-gray-200 hover:bg-gray-50"
                  }`}
                >
                  <Icon
                    className={`w-6 h-6 ${activeMode === value ? "text-secondary" : "text-gray-400"}`}
                  />
                  <div>
                    <h3 className="text-sm font-bold text-gray-900">{title}</h3>
                    <p className="text-xs text-gray-400 mt-1">{desc}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {activeMode === "roleplay" && (
            <div className="space-y-3">
              <label className="block text-sm font-semibold text-gray-700">
                Select Customer Persona
              </label>
              <CommonSelect
                value={selectedPersonaId}
                onChange={(val) => setSelectedPersonaId(val || "")}
                options={personas.map((p) => ({ label: p.name, value: p.id }))}
                width="w-full"
                placeholder="Select Persona"
                classNames={{
                  base: "mt-2",
                }}
              />
              {selectedPersonaId && (
                <div className="p-4 bg-gray-50 rounded-xl border border-gray-100 mt-3 text-xs text-gray-500 font-medium">
                  <span className="block font-bold text-gray-700 mb-1">
                    Persona:{" "}
                    {personas.find((p) => p.id === selectedPersonaId)?.name}
                  </span>
                  {
                    personas.find((p) => p.id === selectedPersonaId)
                      ?.description
                  }
                </div>
              )}
            </div>
          )}

          {activeMode === "learn" && (
            <div className="p-4 bg-gray-50 rounded-xl border border-gray-100 text-xs text-gray-500">
              <span className="font-bold text-gray-700">After training</span>
              <ul className="mt-2 space-y-1 list-disc list-inside">
                <li>
                  <strong>Finish</strong> — end without an assessment
                </li>
                <li>
                  <strong>Switch to Roleplay</strong> — test your skills and get
                  a report
                </li>
              </ul>
            </div>
          )}

          <button
            onClick={startSession}
            disabled={activeMode === "roleplay" && !selectedPersonaId}
            className="w-full inline-flex items-center justify-center px-6 py-3 bg-secondary hover:bg-secondary-light disabled:opacity-50 text-white rounded-xl font-semibold shadow-md transition-all"
          >
            Launch Setup
          </button>
        </div>
      </div>
    );
  }

  // ── 2. Avatar Selection Lobby (Before entering the session active state) ───
  // if (session && !sessionActive) {
  //   return <AvatarSelector onLaunch={handleLobbyLaunch} />;
  // }

  // ── 3. Redesigned Premium Active Session ──────────────────────────────────
  return (
    <div className="relative w-full h-[calc(100vh-40px)] flex flex-col overflow-hidden bg-gray-50 border border-gray-200 rounded-2xl shadow-2xl isolate [transform:translate3d(0,0,0)]">
      {/* Floating Header */}
      <SessionHeader onBack={() => navigate("/ai-workspace?tab=sales-coach")} />

      {/* Main visual panel layout */}
      <div className="flex-1 w-full min-h-0 flex relative">
        <div
          className={`h-full relative transition-all duration-500 ease-in-out ${
            chatOpen ? "w-full md:w-[calc(100%-420px)]" : "w-full"
          }`}
        >
          <AvatarScene />
          <FloatingChatButton />

          {/* Action controls (always visible for quick transition or finish) */}
          {session && (
            <div className="absolute bottom-6 left-6 z-30 flex gap-2">
              {isRoleplay ? (
                <button
                  onClick={handleEndRoleplay}
                  disabled={assessing || !canEndRoleplay}
                  className="px-5 py-2.5 bg-rose-600 hover:bg-rose-500 disabled:opacity-50 text-white rounded-xl flex items-center justify-center gap-1.5 transition-all shadow-lg text-xs font-bold"
                >
                  Finish &amp; Get Assessment
                </button>
              ) : (
                <>
                  <button
                    onClick={() => setShowRoleplaySetup(true)}
                    disabled={!canEndTraining || finishing}
                    className="px-5 py-2.5 bg-secondary hover:bg-secondary-light disabled:opacity-50 text-white rounded-xl flex items-center justify-center gap-1.5 transition-all shadow-lg text-xs font-bold"
                  >
                    Switch to Roleplay
                    <ArrowRight className="w-4 h-4" />
                  </button>
                  <button
                    onClick={handleFinishTraining}
                    disabled={!canEndTraining || finishing}
                    className="px-5 py-2.5 rounded-xl border flex items-center justify-center gap-1.5 bg-white border-gray-200 hover:bg-gray-50 text-gray-700 disabled:opacity-50 transition-all text-xs font-bold shadow-sm"
                  >
                    <LogOut className="w-4 h-4 text-gray-500" />
                    {finishing ? "Finishing…" : "Finish"}
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        {/* Sliding Chat Feed */}
        <AnimatePresence>
          {chatOpen && (
            <ChatPanel
              messages={messages}
              input={input}
              setInput={setInput}
              handleSend={handleSend}
              sending={sending}
              assessing={assessing}
              showRoleplaySetup={showRoleplaySetup}
              onClose={() => setChatOpen(false)}
            />
          )}
        </AnimatePresence>
      </div>

      {/* Floating Action Controls */}

      {/* Assessment overlay (modal) */}
      <AnimatePresence>
        {assessment &&
          createPortal(
            <div className="fixed inset-0 bg-gray-900/60 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="bg-white border border-gray-200 rounded-3xl max-w-2xl w-full max-h-[85vh] flex flex-col shadow-2xl relative text-gray-800 overflow-hidden"
              >
                {/* Fixed Header */}
                <div className="p-6 pb-4 border-b border-gray-100 flex-shrink-0">
                  <h2 className="text-xl font-bold text-gray-900 flex items-center">
                    <Sparkles className="w-5 h-5 mr-2 text-secondary animate-pulse" />
                    Roleplay Assessment Complete
                  </h2>
                  <p className="text-xs text-gray-500 mt-1">
                    Your performance was evaluated from the customer roleplay
                    conversation.
                  </p>
                </div>

                {/* Scrollable Body */}
                <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6 scrollbar-thin scrollbar-thumb-gray-200 scrollbar-track-transparent">
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div className="p-4 bg-secondary/5 border border-secondary/20 rounded-2xl col-span-3 shadow-sm">
                      <span className="text-xs font-bold text-secondary uppercase tracking-wider">
                        Overall Score
                      </span>
                      <div className="text-4xl font-extrabold text-secondary mt-1">
                        {assessment.overall_score ?? assessment.overallScore}%
                      </div>
                    </div>
                    {[
                      {
                        label: "Product Knowledge",
                        val:
                          assessment.product_knowledge_score ??
                          assessment.productKnowledgeScore,
                      },
                      {
                        label: "Objection Handling",
                        val:
                          assessment.objection_handling_score ??
                          assessment.objectionHandlingScore,
                      },
                      {
                        label: "Communication",
                        val:
                          assessment.communication_score ??
                          assessment.communicationScore,
                      },
                      {
                        label: "Feature Understanding",
                        val:
                          assessment.feature_understanding_score ??
                          assessment.featureUnderstandingScore,
                      },
                      {
                        label: "Competitive Positioning",
                        val:
                          assessment.competitive_understanding_score ??
                          assessment.competitiveUnderstandingScore,
                      },
                    ].map((item, idx) => (
                      <div
                        key={idx}
                        className="p-3 bg-gray-50 border border-gray-150 rounded-xl shadow-sm"
                      >
                        <span className="block text-[10px] text-gray-450 uppercase tracking-wider font-bold">
                          {item.label}
                        </span>
                        <span className="text-lg font-bold text-gray-800 mt-1 block">
                          {item.val}%
                        </span>
                      </div>
                    ))}
                  </div>

                  <div className="space-y-6">
                    {[
                      {
                        icon: CheckCircle,
                        color: "text-green-600",
                        label: "Key Strengths",
                        items: assessment.strengths,
                      },
                      {
                        icon: ShieldAlert,
                        color: "text-amber-600",
                        label: "Areas of Improvement",
                        items:
                          assessment.improvement_areas ||
                          assessment.improvementAreas,
                      },
                      {
                        icon: BarChart2,
                        color: "text-secondary",
                        label: "Recommendations",
                        items: assessment.recommendations,
                      },
                    ].map(({ icon: Icon, color, label, items }) => (
                      <div key={label}>
                        <h4
                          className={`text-sm font-bold flex items-center ${color}`}
                        >
                          <Icon className="w-4 h-4 mr-2" />
                          {label}
                        </h4>
                        <ul className="mt-2 list-disc list-inside text-xs text-gray-500 space-y-1.5 pl-2 font-medium">
                          {(items || []).map((s, i) => (
                            <li key={i}>{s}</li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Fixed Footer */}
                <div className="p-6 pt-4 border-t border-gray-100 flex justify-end flex-shrink-0">
                  <button
                    onClick={() => navigate("/ai-workspace?tab=sales-coach")}
                    className="px-6 py-2.5 bg-secondary hover:bg-secondary-light text-white text-sm font-semibold rounded-xl transition-all shadow-md"
                  >
                    Close &amp; Return to Catalog
                  </button>
                </div>
              </motion.div>
            </div>,
            document.body,
          )}
      </AnimatePresence>

      {/* Roleplay setup switch modal */}
      {showRoleplaySetup && (
        <div className="fixed inset-0 bg-gray-900/40 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="bg-white border border-gray-200 p-6 rounded-3xl max-w-sm w-full shadow-2xl relative text-gray-800"
          >
            <div className="w-full space-y-4">
              <div className="text-center">
                <UserCheck className="w-8 h-8 text-secondary mx-auto mb-2" />
                <h3 className="font-bold text-gray-900 text-sm">
                  Choose Customer Persona
                </h3>
                <p className="text-xs text-gray-500 mt-1">
                  Select who you will pitch to in the roleplay.
                </p>
              </div>
              <CommonSelect
                value={selectedPersonaId}
                onChange={(val) => setSelectedPersonaId(val || "")}
                options={personas.map((p) => ({ label: p.name, value: p.id }))}
                width="w-full"
                placeholder="Select Persona"
              />
              {selectedPersonaId && (
                <div className="p-3 bg-gray-50 rounded-xl border border-gray-150 text-xs text-gray-500">
                  {
                    personas.find((p) => p.id === selectedPersonaId)
                      ?.description
                  }
                </div>
              )}
              <button
                onClick={startRoleplaySession}
                disabled={switchingToRoleplay || !selectedPersonaId}
                className="w-full py-2.5 bg-secondary hover:bg-secondary-light disabled:opacity-50 text-white rounded-xl flex items-center justify-center gap-1.5 transition-all shadow-md text-xs font-semibold"
              >
                {switchingToRoleplay ? "Starting roleplay…" : "Start Roleplay"}
                {!switchingToRoleplay && <ArrowRight className="w-4 h-4" />}
              </button>
              <button
                onClick={() => setShowRoleplaySetup(false)}
                disabled={switchingToRoleplay}
                className="w-full py-2 text-xs text-gray-400 hover:text-gray-700"
              >
                Back
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default RepLearningInterface;
